import { Controller } from "@hotwired/stimulus";

// Based on: https://railsblocks.com/docs/marquee
// A horizontally scrollable strip that also drifts along on its own. The list is
// cloned so three copies sit side by side and we park in the middle one, which is
// what lets the drift wrap without the seam ever reaching the viewport.

export default class extends Controller {
  static targets = ["track", "list"];
  static values = {
    speed: { type: Number, default: 20 }, // Seconds to drift one copy's width
    direction: { type: String, default: "left" },
    clones: { type: Number, default: 2 },
    autoplay: { type: Boolean, default: true },
    resumeDelay: { type: Number, default: 900 }, // Quiet time before the drift takes over again
  };

  connect() {
    this.started = this.autoplayValue;
    this.hovering = false;
    this.cloneList();
    this.recentre();

    this.resizeObserver = new ResizeObserver(() => this.recentre());
    this.resizeObserver.observe(this.listTarget);
    this.play();
  }

  disconnect() {
    this.halt();
    this.resizeObserver.disconnect();
    clearTimeout(this.resumeTimer);
    this.removeClones();
  }

  start() {
    this.started = true;
    this.play();
  }

  pause() {
    this.hovering = true;
    this.halt();
  }

  resume() {
    this.hovering = false;
    this.play();
  }

  handOverToReader() {
    if (Math.abs(this.trackTarget.scrollLeft - this.writtenPosition) < 2)
      return;

    this.halt();
    this.position = this.trackTarget.scrollLeft;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => this.play(), this.resumeDelayValue);
  }

  play() {
    if (this.frame || !this.drifting) return;

    this.lastFrameAt = null;
    this.frame = requestAnimationFrame((timestamp) => this.drift(timestamp));
  }

  halt() {
    cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  drift(timestamp) {
    const elapsed =
      this.lastFrameAt === null ? 0 : timestamp - this.lastFrameAt;
    this.lastFrameAt = timestamp;

    this.position += this.pixelsPerMillisecond * elapsed * this.directionSign;
    this.wrap();
    this.scrollTo(this.position);

    this.frame = requestAnimationFrame((next) => this.drift(next));
  }

  // A frame's drift is well under a pixel and scroll offsets snap to whole pixels,
  // so the position is kept here and the leftover fraction is drawn as a translate.
  scrollTo(position) {
    const wholePixels = Math.floor(position);
    this.trackTarget.scrollLeft = wholePixels;
    this.trackTarget.style.setProperty(
      "--drift-fraction",
      position - wholePixels,
    );
    this.writtenPosition = this.trackTarget.scrollLeft;
  }

  recentre() {
    this.position = this.copyWidth;
    this.scrollTo(this.position);
  }

  wrap() {
    const width = this.copyWidth;
    if (width === 0 || (this.position >= width && this.position < width * 2))
      return;

    this.position =
      width + ((((this.position - width) % width) + width) % width);
  }

  cloneList() {
    this.removeClones();

    for (let index = 0; index < this.clonesValue; index++) {
      const clone = this.listTarget.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      clone.classList.add("marquee-clone");
      clone.removeAttribute("data-marquee-target");
      this.trackTarget.appendChild(clone);
    }
  }

  removeClones() {
    this.trackTarget
      .querySelectorAll(".marquee-clone")
      .forEach((clone) => clone.remove());
  }

  get drifting() {
    return this.started && !this.hovering && !this.prefersReducedMotion;
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  get copyWidth() {
    return this.listTarget.getBoundingClientRect().width;
  }

  get pixelsPerMillisecond() {
    return this.copyWidth / (this.speedValue * 1000);
  }

  get directionSign() {
    return this.directionValue === "right" ? -1 : 1;
  }
}
